<script setup lang="ts">
  import RecipientCard from '@/components/drawer/DrawerRecipients/RecipientCard.vue'
  import { AlertTriangleIcon, LinkExternal01Icon, RefreshCw05Icon } from '@gohighlevel/ghl-icons/24/outline'
  import {
    ELEMENTS_META,
    IElement,
    IRecipient,
    isDefaultPaymentProviderConfigured,
    isDefaultPaymentProviderSupportedForSaveCard,
    RecipientEntityTypeEnum,
    SaveCardDetailsElementOptions,
    useTypography,
  } from '@gohighlevel/ghl-proposals-common'
  import { UIButton, UICheckbox, UIDivider, UIFormItem, UIInput, UISelect, UITextSmRegular, UITextXsRegular } from '@gohighlevel/ghl-ui'
  import { computed, onMounted, PropType, ref, toRaw, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useElementOption } from '../../../composable/elementOptions.js'

  import { usePayment } from '@/composition/usePayment.js'
  import { usePostmate } from '@/composition/usePostmate.js'
  import { useAppStore } from '@/store/app.js'
  import { useBuilderStore } from '@/store/builder.js'
  import { useRoute } from 'vue-router'
  const { t } = useI18n()
  const props = defineProps({
    element: {
      type: Object as PropType<IElement<SaveCardDetailsElementOptions>>,
      required: true,
    },
  })
  const route = useRoute()
  const { updateOption } = useElementOption()
  const { syncRecipient, syncCustomFieldsForLinkage } = useBuilderStore()
  const { toCapitalize } = useTypography()
  const { redirectOnParentRoute } = usePostmate()
  const { fetchPaymentIntegrations } = usePayment()
  const recipients = computed(() => appStore.document?.recipients)
  const appStore = useAppStore()
  const isPaymentProviderConfigured = computed(() =>
    isDefaultPaymentProviderConfigured(appStore.paymentIntegrations?.defaultPaymentProvider, appStore.document?.paymentLiveMode ?? true)
  )
  const isPaymentProviderSupportedForSaveCard = computed(() =>
    isDefaultPaymentProviderSupportedForSaveCard(appStore.paymentIntegrations?.defaultPaymentProvider)
  )
  const recipient = ref()
  const selectValue = ref()

  onMounted(() => {
    syncRecipientList()
  })
  const syncRecipientList = () => {
    syncRecipient()
    fetchRecipientData()
    getSelectValue()
    syncCustomFieldsForLinkage()
  }
  const signedByOptions = computed(() => {
    let allRecipients = recipients.value
    // For card save element business user is not allowed by payment widget
    // Filter out all recipients with entityName === USERS (sender and business users)
    allRecipients = allRecipients.filter((recipient: IRecipient) => recipient.entityName !== RecipientEntityTypeEnum.USERS)
    const recipientList =
      allRecipients.map((contact: IRecipient) => ({
        ...contact,
        label: toCapitalize(
          appStore.userId === contact.id
            ? `${contact?.contactName || ''} ${t('common.you')}`
            : contact?.contactName || `${contact.firstName} ${contact.lastName}`
        ).trim(),
        value: `${contact.id}_${contact.entityName}`,
      })) || []
    return recipientList
  })
  const getSelectValue = () => {
    const recipientsList = toRaw(recipients.value)
    const lastSelectedId = props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof SaveCardDetailsElementOptions]
    const lastSelectedEntityName = props.element?.component.options[ELEMENTS_META.ENTITY_NAME as keyof SaveCardDetailsElementOptions]
    // For card save element business user is not allowed by payment widget
    // Clear selection if previously assigned to a user (sender or business user)
    if (lastSelectedEntityName === RecipientEntityTypeEnum.USERS) {
      selectValue.value = null
      return
    }
    const userStillExistsInRecipients = recipientsList.find((el: any) => {
      return el.id === lastSelectedId
    })
    if (lastSelectedId && !userStillExistsInRecipients) {
      selectValue.value = null
      return
    }
    if (
      props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof SaveCardDetailsElementOptions] &&
      props.element?.component.options[ELEMENTS_META.ENTITY_NAME as keyof SaveCardDetailsElementOptions]
    ) {
      selectValue.value = `${props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof SaveCardDetailsElementOptions]}_${
        props.element?.component.options[ELEMENTS_META.ENTITY_NAME as keyof SaveCardDetailsElementOptions]
      }`
    }
  }
  const fetchRecipientData = (recipientId?: string, entityName?: string) => {
    const assignedRecipient = recipients.value.find((elem: IRecipient) => {
      if (recipientId && entityName) {
        return elem.id === recipientId && elem.entityName === entityName
      } else {
        return (
          elem.id === props.element?.component.options[ELEMENTS_META.RECIPIENT as keyof SaveCardDetailsElementOptions] &&
          elem.entityName === props.element?.component.options[ELEMENTS_META.ENTITY_NAME as keyof SaveCardDetailsElementOptions]
        )
      }
    })
    if (assignedRecipient) {
      recipient.value = assignedRecipient
    }
  }
  const assignRecipientToUploadDocumentField = (val: string) => {
    const recipientData = val.split('_')
    if (recipientData.length === 2) {
      const recipientId = recipientData[0]
      const entityName = recipientData[1]
      // For card save element business user is not allowed by payment widget
      // Prevent assigning to any user type (sender or business user)
      if (entityName === RecipientEntityTypeEnum.USERS) {
        selectValue.value = null
        syncRecipientList()
        return
      }
      fetchRecipientData(recipientId, entityName)
      updateOption(ELEMENTS_META.RECIPIENT, recipientId)
      updateOption(ELEMENTS_META.ENTITY_NAME, entityName)
    }
    syncRecipientList()
  }
  watch(props, newValue => {
    if (newValue) {
      syncRecipientList()
    }
  })

  const redirectToPaymentIntegrations = () => {
    redirectOnParentRoute(
      {
        name: 'payment-integrations-v2',
        params: {
          location_id: route.params.locationId as string,
        },
      },
      { newTab: true }
    )
  }
  const loading = ref(false)
  const syncChanges = async () => {
    loading.value = true
    await fetchPaymentIntegrations()
    loading.value = false
  }
</script>

<template>
  <div class="hl-proposal-sidebar-content hl-proposal-textfield-properties">
    <div
      class="p-4 rounded-md border border-warning-300 bg-warning-25 flex items-start gap-2 mb-4"
      v-if="!isPaymentProviderConfigured || !isPaymentProviderSupportedForSaveCard"
    >
      <div class="flex items-center gap-2">
        <AlertTriangleIcon class="h-5 w-5 text-warning-700" />
      </div>
      <div>
        <UITextSmRegular class="font-medium text-warning-700">
          {{
            isPaymentProviderConfigured && !isPaymentProviderSupportedForSaveCard
              ? $t('validations.paymentProvider.unsupportedSaveCardTitle')
              : $t('validations.paymentProvider.required')
          }}
        </UITextSmRegular>
        <UITextSmRegular class="text-warning-500 mt-2">
          {{
            isPaymentProviderConfigured && !isPaymentProviderSupportedForSaveCard
              ? $t('validations.paymentProvider.unsupportedSaveCardDescription')
              : $t('validations.paymentProvider.description')
          }}
        </UITextSmRegular>
        <div class="flex items-center gap-4 mt-2">
          <UIButton id="connect-btn" :text="true" @click="redirectToPaymentIntegrations">
            {{ $t('validations.paymentProvider.connect') }}
            <LinkExternal01Icon class="h-4 w-4 ml-2" />
          </UIButton>

          <UIButton id="sync-changes-btn" :text="true" @click="syncChanges" :loading="loading">
            {{ $t('validations.paymentProvider.syncChanges') }}
            <RefreshCw05Icon class="h-4 w-4 ml-2" />
          </UIButton>
        </div>
      </div>
    </div>
    <div class="mb-4">
      <UIFormItem :show-feedback="false" :label="$t('common.toBeFilledBy')">
        <UISelect
          id="CollectDocumentsElement"
          :placeholder="$t('common.assignRecipientToCardField')"
          :options="signedByOptions"
          type="avatar"
          :value="selectValue"
          @update:value="(val: any) => assignRecipientToUploadDocumentField(val)"
        />
      </UIFormItem>
      <div>
        <RecipientCard :recipient="recipient" />
      </div>
    </div>
    <UICheckbox
      class="mb-4"
      :id="`${element?.id}-required-checkbox`"
      :disabled="false"
      :checked="element?.component.options.required"
      @update:checked="
        (val: boolean) => {
          updateOption(ELEMENTS_META.REQUIRED, val, element?.id)
        }
      "
    >
      {{ $t('common.required') }}
    </UICheckbox>

    <UITextXsRegular class="font-medium text-gray-400">
      {{ $t('common.options') }}
    </UITextXsRegular>
    <UIDivider />
    <UIFormItem :label="$t('common.placeholder')">
      <UIInput
        :id="`${element?.id}-placeholder`"
        :model-value="element?.component.options.placeholder"
        :placeholder="$t('common.provideCardDetails')"
        @update:model-value="val => updateOption(ELEMENTS_META.PLACEHOLDER, val)"
      />
    </UIFormItem>
  </div>
</template>

<style lang="scss">
  .n-avatar__text {
    transform: translateX(-50%) translateY(-50%) scale(1);
  }
  .hl-proposal-textfield-properties {
    .n-divider.n-divider--no-title {
      margin: 10px 0px;
    }
  }
</style>
